/**
 * Chart of accounts.
 *
 * A chart is a tree and reads as one: rows arrive in materialized-path order,
 * each is indented by its depth, and a node nobody can post to is shaded as
 * the heading it is.
 *
 * An account carries two names. The English one is required because the
 * contract requires it; the Arabic one is optional but is captured in the same
 * form, beside it, so a chart is written bilingually at the moment of creation
 * rather than translated afterwards. The list shows whichever name matches the
 * reader's language and falls back to the other rather than to a blank.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Pencil, Plus } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { localizedName } from "@/lib/i18n";
import { ApiError } from "@/lib/api/errors";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  ACCOUNT_TYPES,
  DEFAULT_NORMAL_BALANCE,
  NORMAL_BALANCES,
  createAccount,
  getAccount,
  updateAccount,
  type Account,
  type AccountCreate,
  type AccountPatch,
  type AccountType,
  type NormalBalance,
} from "../api";
import { ledgerKeys, useAccounts } from "../queries";

const MAX_NAME = 200;
const CURRENCY_RE = /^[A-Z]{3}$/;

interface AccountDraft {
  code: string;
  name: string;
  nameAr: string;
  accountType: AccountType;
  normalBalance: NormalBalance;
  parentId: string;
  isPostable: boolean;
  currencyRestriction: string;
  active: boolean;
}

const EMPTY_DRAFT: AccountDraft = {
  code: "",
  name: "",
  nameAr: "",
  accountType: "asset",
  normalBalance: "debit",
  parentId: "",
  isPostable: true,
  currencyRestriction: "",
  active: true,
};

/** The root option cannot be the empty string: an empty Select value reads as
 *  "nothing chosen" to Radix and the item would be unselectable. */
const NO_PARENT = "__root__";

type DraftErrors = Partial<Record<keyof AccountDraft, string>>;

function depthOf(account: Account): number {
  return account.path.split("/").length - 1;
}

/**
 * The name in the OTHER language — the one the reader is not working in. The
 * column exists so both names are visible at once on the screen that edits
 * them, which is exactly where a missing translation should be obvious.
 */
function otherName(account: Account, language: string): string | null {
  return language.startsWith("ar") ? account.name : (account.name_ar ?? null);
}

export interface AccountsPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
}

export function AccountsPage({ canCreate = true, canUpdate = true }: AccountsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const accountsQuery = useAccounts();
  const accounts = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data]);

  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<{ account: Account; etag: string | null } | null>(null);
  const [draft, setDraft] = useState<AccountDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<DraftErrors>({});

  /**
   * The whole chart is in memory, so the filter runs here rather than as a
   * server query: asking the server for matches alone would return orphans —
   * children whose parents were filtered out have nothing to indent from.
   * A match therefore keeps its ancestors on screen.
   */
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === "") return accounts;
    const keep = new Set<string>();
    for (const account of accounts) {
      const haystack = `${account.code} ${account.name} ${account.name_ar ?? ""}`.toLowerCase();
      if (!haystack.includes(needle)) continue;
      let path = account.path;
      keep.add(path);
      // Every prefix of a materialized path is an ancestor's path.
      while (path.includes("/")) {
        path = path.slice(0, path.lastIndexOf("/"));
        keep.add(path);
      }
    }
    return accounts.filter((account) => keep.has(account.path));
  }, [accounts, search]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ledgerKeys.accounts.all });
  };

  const openCreate = (): void => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setErrors({});
    setDialogOpen(true);
  };

  const loadForEdit = useMutation({
    mutationFn: (accountId: string) => getAccount(accountId),
    onSuccess: ({ data, etag }) => {
      setEditing({ account: data, etag });
      setDraft({
        code: data.code,
        name: data.name,
        nameAr: data.name_ar ?? "",
        accountType: data.account_type,
        normalBalance: data.normal_balance,
        parentId: data.parent_id ?? "",
        isPostable: data.is_postable,
        currencyRestriction: data.currency_restriction ?? "",
        active: data.active,
      });
      setErrors({});
      setDialogOpen(true);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const onWriteError = (error: unknown): void => {
    if (error instanceof ApiError && (error.status === 409 || error.status === 422)) {
      setErrors({ code: error.problem?.detail ?? t("errors.generic") });
      return;
    }
    surfaceProblem(error, t);
  };

  const createMutation = useIdempotentMutation<Account, AccountCreate>({
    mutationFn: (input, key) => createAccount(input, key),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: onWriteError,
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; patch: AccountPatch; ifMatch: string }) =>
      updateAccount(input.id, input.patch, input.ifMatch),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: onWriteError,
  });

  const validate = (): boolean => {
    const next: DraftErrors = {};
    if (draft.code.trim() === "") next.code = t("ledger.accounts.codeRequired");
    if (draft.name.trim() === "") next.name = t("ledger.accounts.nameRequired");
    if ([...draft.name.trim()].length > MAX_NAME) next.name = t("ledger.accounts.nameTooLong");
    if ([...draft.nameAr.trim()].length > MAX_NAME) next.nameAr = t("ledger.accounts.nameTooLong");
    const currency = draft.currencyRestriction.trim().toUpperCase();
    if (currency !== "" && !CURRENCY_RE.test(currency)) {
      next.currencyRestriction = t("ledger.accounts.currencyInvalid");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (): void => {
    if (!validate()) return;
    const nameAr = draft.nameAr.trim();
    const currency = draft.currencyRestriction.trim().toUpperCase();

    if (editing === null) {
      createMutation.mutate({
        code: draft.code.trim(),
        name: draft.name.trim(),
        ...(nameAr === "" ? {} : { name_ar: nameAr }),
        account_type: draft.accountType,
        normal_balance: draft.normalBalance,
        is_postable: draft.isPostable,
        active: draft.active,
        ...(draft.parentId === "" ? {} : { parent_id: draft.parentId }),
        ...(currency === "" ? {} : { currency_restriction: currency }),
      });
      return;
    }

    // A merge patch says only what changed: an absent member is "leave it",
    // an explicit null is "clear it". Sending the whole record instead would
    // re-assert values another operator may have edited since this load.
    const current = editing.account;
    const patch: Record<string, unknown> = {};
    if (draft.code.trim() !== current.code) patch.code = draft.code.trim();
    if (draft.name.trim() !== current.name) patch.name = draft.name.trim();
    if (nameAr !== (current.name_ar ?? "")) patch.name_ar = nameAr === "" ? null : nameAr;
    if (draft.accountType !== current.account_type) patch.account_type = draft.accountType;
    if (draft.normalBalance !== current.normal_balance) patch.normal_balance = draft.normalBalance;
    if (draft.isPostable !== current.is_postable) patch.is_postable = draft.isPostable;
    if (draft.active !== current.active) patch.active = draft.active;
    if (draft.parentId !== (current.parent_id ?? "")) {
      patch.parent_id = draft.parentId === "" ? null : draft.parentId;
    }
    if (currency !== (current.currency_restriction ?? "")) {
      patch.currency_restriction = currency === "" ? null : currency;
    }

    if (Object.keys(patch).length === 0) {
      setDialogOpen(false);
      return;
    }
    if (editing.etag === null) {
      surfaceProblem(new Error(t("errors.generic")), t);
      return;
    }
    updateMutation.mutate({ id: current.id, patch, ifMatch: editing.etag });
  };

  const saving = createMutation.isPending || updateMutation.isPending;
  const title = t("ledger.accounts.title");

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="flex-1 text-2xl font-semibold tracking-tight">{title}</h1>
        <Input
          className="w-64"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("ledger.accounts.search")}
          aria-label={t("ledger.accounts.search")}
        />
        {canCreate ? (
          <Button onClick={openCreate}>
            <Plus className="me-2 size-4" aria-hidden="true" />
            {t("ledger.accounts.new")}
          </Button>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          {accountsQuery.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {t("errors.generic")}
            </p>
          ) : accountsQuery.isPending ? (
            <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("table.empty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-start text-xs font-medium text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">{t("ledger.accounts.code")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("ledger.accounts.name")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("ledger.accounts.otherName")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("ledger.accounts.type")}</th>
                    <th className="px-4 py-2.5 font-medium">
                      {t("ledger.accounts.normalBalance")}
                    </th>
                    <th className="px-4 py-2.5 font-medium">{t("ledger.accounts.status")}</th>
                    <th className="px-4 py-2.5 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((account) => (
                    <tr
                      key={account.id}
                      className={
                        account.is_postable
                          ? "border-b transition-colors last:border-0 hover:bg-muted/40"
                          : "border-b bg-muted/60 font-medium last:border-0"
                      }
                    >
                      <td className="px-4 py-3 align-top font-mono text-xs">{account.code}</td>
                      <td className="px-4 py-3 align-top">
                        {/* Logical padding, so the tree indents from the start
                            edge in Arabic as well as English. */}
                        <span
                          style={{ paddingInlineStart: `${depthOf(account) * 1.25}rem` }}
                          className="block"
                        >
                          {localizedName(account.name, account.name_ar, i18n.language)}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3 align-top text-muted-foreground"
                        // The Arabic name reads right-to-left whichever way
                        // round the two columns are.
                        dir={i18n.language.startsWith("ar") ? "ltr" : "rtl"}
                      >
                        {otherName(account, i18n.language) ?? <span>—</span>}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {t(`ledger.accounts.types.${account.account_type}`)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {t(`ledger.accounts.balances.${account.normal_balance}`)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Badge variant={account.active ? "default" : "secondary"}>
                          {account.active
                            ? t("ledger.accounts.active")
                            : t("ledger.accounts.inactive")}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 align-top text-end">
                        {canUpdate ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={loadForEdit.isPending}
                            onClick={() => loadForEdit.mutate(account.id)}
                            aria-label={`${t("ledger.accounts.edit")} ${account.code}`}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing === null ? t("ledger.accounts.new") : t("ledger.accounts.edit")}
            </DialogTitle>
            <DialogDescription>{t("ledger.accounts.formHint")}</DialogDescription>
          </DialogHeader>

          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <Field label={t("ledger.accounts.code")} error={errors.code} htmlFor="account-code">
              <Input
                id="account-code"
                dir="ltr"
                value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </Field>

            <Field label={t("ledger.accounts.nameEn")} error={errors.name} htmlFor="account-name">
              <Input
                id="account-name"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </Field>

            {/* The Arabic field is right-to-left whatever language the operator
                is working in — it holds Arabic either way. */}
            <Field
              label={t("ledger.accounts.nameAr")}
              error={errors.nameAr}
              htmlFor="account-name-ar"
              hint={t("ledger.accounts.nameArHint")}
            >
              <Input
                id="account-name-ar"
                dir="rtl"
                lang="ar"
                value={draft.nameAr}
                onChange={(event) => setDraft({ ...draft, nameAr: event.target.value })}
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label={t("ledger.accounts.type")} htmlFor="account-type">
                <Select
                  value={draft.accountType}
                  onValueChange={(value) =>
                    setDraft({
                      ...draft,
                      accountType: value as AccountType,
                      // The normal balance follows the type unless the operator
                      // has already made it a contra account on purpose.
                      normalBalance: DEFAULT_NORMAL_BALANCE[value as AccountType],
                    })
                  }
                >
                  <SelectTrigger id="account-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`ledger.accounts.types.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label={t("ledger.accounts.normalBalance")} htmlFor="account-balance">
                <Select
                  value={draft.normalBalance}
                  onValueChange={(value) =>
                    setDraft({ ...draft, normalBalance: value as NormalBalance })
                  }
                >
                  <SelectTrigger id="account-balance">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NORMAL_BALANCES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`ledger.accounts.balances.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label={t("ledger.accounts.parent")} htmlFor="account-parent">
              <Select
                value={draft.parentId === "" ? NO_PARENT : draft.parentId}
                onValueChange={(value) =>
                  setDraft({ ...draft, parentId: value === NO_PARENT ? "" : value })
                }
              >
                <SelectTrigger id="account-parent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PARENT}>{t("ledger.accounts.noParent")}</SelectItem>
                  {accounts
                    .filter((candidate) => candidate.id !== editing?.account.id)
                    .map((candidate) => (
                      <SelectItem key={candidate.id} value={candidate.id}>
                        {candidate.code} —{" "}
                        {localizedName(candidate.name, candidate.name_ar, i18n.language)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label={t("ledger.accounts.currency")}
              error={errors.currencyRestriction}
              htmlFor="account-currency"
              hint={t("ledger.accounts.currencyHint")}
            >
              <Input
                id="account-currency"
                dir="ltr"
                maxLength={3}
                value={draft.currencyRestriction}
                onChange={(event) =>
                  setDraft({ ...draft, currencyRestriction: event.target.value.toUpperCase() })
                }
              />
            </Field>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.isPostable}
                onCheckedChange={(value) => setDraft({ ...draft, isPostable: value === true })}
              />
              {t("ledger.accounts.postable")}
            </label>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.active}
                onCheckedChange={(value) => setDraft({ ...draft, active: value === true })}
              />
              {t("ledger.accounts.activeLabel")}
            </label>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t("actions.cancel")}
              </Button>
              <Button type="submit" disabled={saving}>
                {t("actions.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint !== undefined && error === undefined ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
      {error !== undefined ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
